import React, { type ChangeEvent, type FC, useEffect, useMemo, useState } from 'react';
import { useSignature, useSignatureActions } from '../../../hooks/api/Signature/useSignature';
import { getErrorMessages } from '../../../utility/team';
import { classNames } from '../../../utility/utility';
import {
  CreatedVia, fileToSignaturePng, initialsFrom, renderTypedPng, SIGNATURE_FONTS, SIGNING_CONSENT_TEXT,
} from '../../../utility/signature';
import DrawPad from './DrawPad';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const MODES: { key: CreatedVia; label: string }[] = [
  { key: 'draw', label: 'Draw' },
  { key: 'type', label: 'Type' },
  { key: 'upload', label: 'Upload' },
];

const Preview: FC<{ title: string; image: string | null }> = ({ title, image }) => (
  <div>
    <p className='text-sm font-medium text-gray-900'>{title}</p>
    <div className='mt-1 flex h-24 items-center justify-center rounded-md bg-white ring-1 ring-inset ring-gray-200'>
      {image ? <img src={image} alt={title} className='max-h-20 max-w-full' /> : <span className='text-sm text-gray-400'>Not set yet</span>}
    </div>
  </div>
);

const UploadInput: FC<{ label: string; onImage: (png: string | null) => void; onError: (message: string) => void }> = ({ label, onImage, onError }) => {
  const handle = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      onImage(await fileToSignaturePng(file));
    } catch (err) {
      onImage(null);
      onError((err as Error).message);
    }
  };
  return (
    <div>
      <label className='block text-sm font-medium text-gray-900'>
        {label}
        <input type='file' accept='image/png,image/jpeg' className='mt-1 block w-full text-sm text-gray-700' onChange={handle} />
      </label>
    </div>
  );
};

// Configuration > Signatures. One saved signature and one set of initials per person; used to sign
// Automatic Contracting packages in one click (by you, or by an agent you allow on the Team page).
const SignaturesPage: FC = () => {
  const { signature, isLoading } = useSignature();
  const { save, remove } = useSignatureActions();

  const [ fullName, setFullName ] = useState('');
  const [ initials, setInitials ] = useState('');
  const [ mode, setMode ] = useState<CreatedVia>('draw');
  const [ fontKey, setFontKey ] = useState(SIGNATURE_FONTS[0].key);
  const [ drawnSignature, setDrawnSignature ] = useState<string | null>(null);
  const [ drawnInitials, setDrawnInitials ] = useState<string | null>(null);
  const [ uploadedSignature, setUploadedSignature ] = useState<string | null>(null);
  const [ uploadedInitials, setUploadedInitials ] = useState<string | null>(null);
  const [ consent, setConsent ] = useState(false);
  const [ editing, setEditing ] = useState(false);
  const [ localError, setLocalError ] = useState<string | null>(null);
  const [ saved, setSaved ] = useState(false);

  useEffect(() => {
    if (signature) setFullName((current) => current || signature.full_name);
  }, [ signature ]);

  // Initials follow the name until the person types their own.
  const [ initialsTouched, setInitialsTouched ] = useState(false);
  useEffect(() => {
    if (!initialsTouched) setInitials(initialsFrom(fullName));
  }, [ fullName, initialsTouched ]);

  const font = SIGNATURE_FONTS.find((f) => f.key === fontKey) ?? SIGNATURE_FONTS[0];
  const typedSignature = useMemo(() => (mode === 'type' ? renderTypedPng(fullName, font) : null), [ mode, fullName, font ]);
  const typedInitials = useMemo(() => (mode === 'type' ? renderTypedPng(initials, font) : null), [ mode, initials, font ]);

  const chosen = {
    draw: { signature: drawnSignature, initials: drawnInitials },
    type: { signature: typedSignature, initials: typedInitials },
    upload: { signature: uploadedSignature, initials: uploadedInitials },
  }[mode];

  const canSave = !!fullName.trim() && !!chosen.signature && !!chosen.initials && consent && !save.isPending;
  const showForm = editing || (!isLoading && !signature);

  const handleSave = async () => {
    setLocalError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        full_name: fullName.trim(), created_via: mode, signature_image: chosen.signature as string,
        initials_image: chosen.initials as string, consent,
      });
      setEditing(false);
      setConsent(false);
      setSaved(true);
    } catch {
      // the server's message is shown below from the mutation state
    }
  };

  return (
    <div className='px-2'>
      <h1 className='text-base font-semibold leading-6 text-gray-900'>Signatures</h1>
      <p className='mt-2 max-w-3xl text-sm text-gray-700'>
        Set up your signature and initials once. When you create contracts you can sign them in one click: your signature
        and initials are placed on the right lines automatically. If you work with an agent, you decide on the Team page
        whether they may apply your signature for you, and you can turn that off at any time.
      </p>

      {isLoading && <p className='mt-6 text-sm text-gray-600'>Loading...</p>}

      {signature && !editing && (
        <section className='mt-6 max-w-3xl' aria-label='Saved signature'>
          <div className='grid gap-4 sm:grid-cols-2'>
            <Preview title='Your signature' image={signature.signature_image} />
            <Preview title='Your initials' image={signature.initials_image} />
          </div>
          <p className='mt-2 text-sm text-gray-500'>
            Saved as {signature.full_name} on {new Date(signature.consented_at).toLocaleDateString()}.
          </p>
          {saved && <p className='mt-1 text-sm text-green-600'>Signature saved.</p>}
          <div className='mt-4 flex gap-x-3'>
            <button type='button' className={secondaryButton} onClick={() => { setEditing(true); setSaved(false); }}>Change</button>
            <button
              type='button'
              className={secondaryButton}
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm('Remove your saved signature? Any agent permission to sign for you will also be turned off.')) remove.mutate();
              }}
            >
              Remove
            </button>
          </div>
        </section>
      )}

      {showForm && (
        <section className='mt-6 max-w-3xl' aria-label='Create signature'>
          <div className='grid gap-4 sm:grid-cols-3'>
            <div className='sm:col-span-2'>
              <label htmlFor='signature_full_name' className='block text-sm font-medium text-gray-900'>Full legal name</label>
              <input id='signature_full_name' className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div>
              <label htmlFor='signature_initials' className='block text-sm font-medium text-gray-900'>Initials</label>
              <input
                id='signature_initials'
                className={inputClass}
                maxLength={4}
                value={initials}
                onChange={(e) => { setInitialsTouched(true); setInitials(e.target.value); }}
              />
            </div>
          </div>

          <div role='tablist' aria-label='How to make your signature' className='mt-6 flex gap-x-2 border-b border-gray-200'>
            {MODES.map((m) => (
              <button
                key={m.key}
                type='button'
                role='tab'
                aria-selected={mode === m.key}
                className={classNames('px-3 py-2 text-sm font-medium', mode === m.key ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700')}
                onClick={() => { setMode(m.key); setLocalError(null); }}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className='mt-4 space-y-4'>
            {mode === 'draw' && (
              <>
                <DrawPad label='Draw your signature' onChange={setDrawnSignature} />
                <DrawPad label='Draw your initials' height={120} onChange={setDrawnInitials} />
              </>
            )}
            {mode === 'type' && (
              <>
                <div role='radiogroup' aria-label='Signature style' className='grid gap-3 sm:grid-cols-2'>
                  {SIGNATURE_FONTS.map((f) => (
                    <button
                      key={f.key}
                      type='button'
                      role='radio'
                      aria-checked={f.key === fontKey}
                      className={classNames('rounded-md p-3 text-left ring-1 ring-inset', f.key === fontKey ? 'ring-2 ring-indigo-600' : 'ring-gray-300 hover:bg-gray-50')}
                      onClick={() => setFontKey(f.key)}
                    >
                      <span className='block text-xs text-gray-500'>{f.label}</span>
                      <span className='block truncate text-3xl text-gray-900' style={{ fontFamily: f.family }}>{fullName || 'Your name'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {mode === 'upload' && (
              <>
                <p className='text-sm text-gray-600'>Upload a clear photo or scan of your signature on white paper. We remove the background for you.</p>
                <UploadInput label='Signature image' onImage={setUploadedSignature} onError={setLocalError} />
                <UploadInput label='Initials image' onImage={setUploadedInitials} onError={setLocalError} />
              </>
            )}
          </div>

          <div className='mt-6 grid gap-4 sm:grid-cols-2'>
            <Preview title='Signature preview' image={chosen.signature} />
            <Preview title='Initials preview' image={chosen.initials} />
          </div>

          <label className='mt-6 flex items-start gap-x-2 text-sm text-gray-700'>
            <input type='checkbox' className='mt-1' checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{SIGNING_CONSENT_TEXT}</span>
          </label>

          {(localError || save.error) && (
            <ul role='alert' className='mt-3 text-sm text-red-600'>
              {localError && <li>{localError}</li>}
              {save.error && getErrorMessages(save.error).map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}

          <div className='mt-4 flex gap-x-3'>
            <button type='button' className={primaryButton} disabled={!canSave} onClick={handleSave}>
              {save.isPending ? 'Saving...' : 'Save signature'}
            </button>
            {signature && <button type='button' className={secondaryButton} onClick={() => setEditing(false)}>Cancel</button>}
          </div>
        </section>
      )}
    </div>
  );
};

export default SignaturesPage;
